import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Create axios instance with interceptors for auth
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds for file operations
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface BucketCreateRequest {
  name: string;
  public?: boolean;
  description?: string;
}

export interface FileUploadRequest {
  bucket?: string;
  key?: string;
  contentType?: string;
  metadata?: Record<string, any>;
  public?: boolean;
}

export interface ListObjectsParams {
  prefix?: string;
  delimiter?: string;
  maxKeys?: string;
  continuationToken?: string;
}

export interface PresignedUrlRequest {
  bucket: string;
  key: string;
  operation?: 'GET' | 'PUT' | 'DELETE';
  expiresIn?: number;
}

export const fileManagementApi = {
  // Bucket operations
  async createBucket(data: BucketCreateRequest) {
    return api.post('/file-management/buckets', data);
  },

  async listBuckets() {
    return api.get('/file-management/buckets');
  },

  async deleteBucket(bucketName: string) {
    return api.delete(`/file-management/buckets/${bucketName}`);
  },

  // File operations
  async uploadFile(bucket: string, formData: FormData) {
    return api.post(`/file-management/buckets/${bucket}/objects`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          // Progress tracking could be implemented here
        }
      },
    });
  },

  async listObjects(bucket: string, params?: ListObjectsParams) {
    return api.get(`/file-management/buckets/${bucket}/objects`, { params });
  },

  async downloadFile(bucket: string, key: string) {
    return api.get(`/file-management/buckets/${bucket}/objects/${key}`, {
      responseType: 'blob',
    });
  },

  async getFileMetadata(bucket: string, key: string) {
    return api.get(`/file-management/buckets/${bucket}/objects/${key}/metadata`);
  },

  async deleteFile(bucket: string, key: string) {
    return api.delete(`/file-management/buckets/${bucket}/objects/${key}`);
  },

  async copyFile(sourceBucket: string, sourceKey: string, destBucket: string, destKey: string) {
    return api.put(`/file-management/buckets/${destBucket}/objects/${destKey}`, null, {
      params: {
        'x-amz-copy-source': `${sourceBucket}/${sourceKey}`,
      },
    });
  },

  // Public file access
  async getPublicFile(bucket: string, key: string) {
    return api.get(`/file-management/public/${bucket}/${key}`, {
      responseType: 'blob',
    });
  },

  async getPrivateFile(bucket: string, key: string) {
    return api.get(`/file-management/private/${bucket}/${key}`, {
      responseType: 'blob',
    });
  },

  // Presigned URLs
  async generatePresignedUrl(data: PresignedUrlRequest) {
    return api.post('/file-management/presigned-url', data);
  },

  // Utility endpoints
  async getHealth() {
    return api.get('/file-management/health');
  },

  async getStats() {
    return api.get('/file-management/stats');
  },

  // Helper method to get file URL for preview
  getFileUrl(bucket: string, key: string, isPublic = false) {
    const baseUrl = API_BASE_URL;
    if (isPublic) {
      return `${baseUrl}/file-management/public/${bucket}/${key}`;
    }
    return `${baseUrl}/file-management/private/${bucket}/${key}`;
  },

  // Helper method to get file preview URL with auth headers
  getAuthenticatedFileUrl(bucket: string, key: string) {
    const token = localStorage.getItem('token');
    const baseUrl = API_BASE_URL;
    const url = `${baseUrl}/file-management/buckets/${bucket}/objects/${key}`;
    
    if (token) {
      // For preview purposes, we'll need to handle auth differently
      // This is a placeholder - actual implementation may vary based on backend setup
      return `${url}?token=${encodeURIComponent(token)}`;
    }
    
    return url;
  },

  // Bulk operations
  async bulkDelete(bucket: string, keys: string[]) {
    // Since there's no native bulk delete in our API, we'll do sequential deletes
    const deletePromises = keys.map(key => this.deleteFile(bucket, key));
    return Promise.allSettled(deletePromises);
  },

  async bulkDownload(bucket: string, keys: string[]) {
    const downloadPromises = keys.map(async (key) => {
      const response = await this.downloadFile(bucket, key);
      return {
        key,
        data: response.data,
        filename: key.split('/').pop() || key,
      };
    });
    return Promise.allSettled(downloadPromises);
  },
};

export default fileManagementApi;